import { Component, inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  FormControl,
  FormGroup,
  Validators,
  AbstractControl,
} from '@angular/forms';

// Importaciones de entornos.
import { environment } from '../../../environments/environment';

// Importaciones de servicios.
import { HostService } from '../../services/host.service';
import { LogService } from '../../services/log.service';

// Importaciones de interfaces.
import { IHost } from '../../interfaces/host.interface';

// Importaciones de modelos.
import { Host } from '../../models/host.model';
import { Log } from '../../models/log.model';

/**
 * Componente de diálogo para la actualización del host.
 *
 * Este componente proporciona una interfaz para actualizar la información de un host.
 * Se muestra como un cuadro de diálogo y permite la edición de los datos de un host seleccionada.
 */
@Component({
  selector: 'app-host-update-dialog',
  templateUrl: './host-update-dialog.component.html',
  standalone: false,
})
export class HostUpdateDialogComponent implements OnInit {
  /**
   * Referencia al diálogo actual.
   *
   * Se utiliza para controlar y cerrar el cuadro de diálogo de actualización del host.
   */
  private readonly dialogRef: MatDialogRef<HostUpdateDialogComponent> = inject(
    MatDialogRef<HostUpdateDialogComponent>
  );

  /**
   * Datos del host.
   *
   * Contiene la información del host que se está actualizando, obtenida a través de la inyección de dependencias.
   */
  private readonly host: IHost = inject<IHost>(MAT_DIALOG_DATA);

  /**
   * Indica si la operación de actualizar está en curso.
   *
   * Se utiliza para deshabilitar la interfaz y mostrar un indicador de carga mientras se actualiza la información del host.
   */
  loading: boolean = false;

  /**
   * Lista de errores al actualizar.
   *
   * Contiene los mensajes de error generados si la operación de guardado falla.
   */
  errors: string[] = [];

  /**
   * Formulario de actualización del host.
   *
   * Gestiona los campos y validaciones necesarias para actualizar la información del host.
   * Se inicializa como un `FormGroup` y contiene los controles correspondientes a los datos editables.
   */
  form: FormGroup = new FormGroup({
    id: new FormControl('', Validators.required),
    name: new FormControl('', Validators.required),
    type: new FormControl('', Validators.required),
    status: new FormControl(true, Validators.required),
  });

  /**
   * Obtiene el control del formulario para el campo 'id'.
   *
   * Retorna el control asociado a la propiedad 'id' del formulario,
   * que gestiona la identificación del host.
   *
   * @returns El control de formulario para 'id'.
   */
  get formId(): AbstractControl | null {
    return this.form.get('id');
  }

  /**
   * Obtiene el control del formulario para el campo 'name'.
   *
   * Retorna el control asociado a la propiedad 'name' del formulario,
   * que gestiona el nombre del host.
   *
   * @returns El control de formulario para 'name'.
   */
  get formName(): AbstractControl | null {
    return this.form.get('name');
  }

  /**
   * Obtiene el control del formulario para el campo 'type'.
   *
   * Retorna el control asociado a la propiedad 'type' del formulario,
   * que gestiona el tipo del host.
   *
   * @returns El control de formulario para 'type'.
   */
  get formType(): AbstractControl | null {
    return this.form.get('type');
  }

  /**
   * Obtiene el control del formulario correspondiente al campo 'status'.
   *
   * Este control indica si el empleado está deshabilitado o no.
   *
   * @returns El control del formulario asociado al campo 'status', o null si no está disponible.
   */
  get formStatus(): AbstractControl | null {
    return this.form.get('status');
  }

  /**
   * Constructor del componente.
   *
   * Este constructor inyecta las dependencias necesarias para el funcionamiento del componente.
   *
   * @param hostService - Servicio para gestionar la información de los hosts.
   * @param logService - Servicio para gestionar los registros.
   * @param snackBar - Servicio para mostrar notificaciones de tipo snack bar.
   */
  constructor(
    private hostService: HostService,
    private logService: LogService,
    private snackBar: MatSnackBar
  ) {}

  /**
   * Método de inicialización del componente.
   *
   * Este método se ejecuta cuando el componente es inicializado. Es utilizado para realizar tareas
   * de configuración o cargar datos necesarios para la operación del componente.
   */
  async ngOnInit(): Promise<void> {
    this.formId?.setValue(this.host.id);
    this.formName?.setValue(this.host.name);
    this.formType?.setValue(this.host.type);
    this.formStatus?.setValue(!this.host.disabled);
  }

  /**
   * Actualiza los datos del host.
   *
   * Este método se encarga de realizar el proceso de actualizar el host.
   * Puede incluir validaciones, llamadas a servicios externos y manejo de errores.
   */
  async update(): Promise<void> {
    this.loading = true;
    this.errors = [];

    const host: IHost = new Host({
      id: Number(this.formId?.value),
      name: this.formName?.value,
      type: this.formType?.value,
      disabled: Number(!this.formStatus?.value),
    });

    try {
      const hostResponse: string = await this.hostService.update(host);

      // Añadir un registro de actualización del host.
      try {
        await this.logService.create(
          new Log({
            description: `Ha actualizado el host '${this.host.id}'.`,
          })
        );
      } catch (errors: any) {
        if (!environment.production) {
          console.warn(
            'No se ha podido guardar el registro de actualización del host.'
          );
        }
      }

      this.snackBar.open(hostResponse, '', {
        duration: 5000,
      });

      this.dialogRef.close(true);
    } catch (errors: any) {
      this.errors = errors;
    }

    this.loading = false;
  }
}
